import React, { useState } from 'react';
import { GameMode, AIDifficulty, LobbyGame } from '../types';
import { WebSocketService } from '../services/WebSocketService';
import './GameModeSelector.css';

interface GameModeSelectorProps {
  onSinglePlayer?: (difficulty: AIDifficulty) => void;
  onSinglePlayerStart?: (difficulty: AIDifficulty) => void;
  onMultiplayer?: () => void;
  onMultiplayerCreate?: () => void;
  onMultiplayerJoin?: (roomCode: string) => void;
  onJoinGame?: () => void;
  onViewStats?: () => void;
  onSettings?: () => void;
  onLobbyGameSelect?: (game: LobbyGame) => void;
  lobbyGames?: LobbyGame[];
  isLoading?: boolean;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

const GameModeSelector: React.FC<GameModeSelectorProps> = ({
  onSinglePlayer,
  onSinglePlayerStart,
  onMultiplayer,
  onMultiplayerCreate,
  onMultiplayerJoin,
  onJoinGame,
  onViewStats,
  onSettings,
  onLobbyGameSelect,
  lobbyGames = [],
  isLoading = false,
  guestGamesRemaining = 3,
  isAuthenticated = false
}) => {
  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null);
  const [roomCode, setRoomCode] = useState('');
  const [roomCodeError, setRoomCodeError] = useState('');

  const handleSinglePlayerClick = () => {
    setSelectedMode(GameMode.SINGLE_PLAYER);
  };

  const handleMultiplayerClick = () => {
    if (onMultiplayer) {
      onMultiplayer();
    } else {
      setSelectedMode(GameMode.MULTIPLAYER);
    }
  };

  const handleBackClick = () => {
    setSelectedMode(null);
    setRoomCode('');
    setRoomCodeError('');
  };

  const handleDifficultySelect = (difficulty: AIDifficulty) => {
    if (onSinglePlayer) {
      onSinglePlayer(difficulty);
    } else if (onSinglePlayerStart) {
      onSinglePlayerStart(difficulty);
    }
  };

  const handleRoomCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 6) {
      setRoomCode(value);
      setRoomCodeError('');
    }
  };

  const handleRoomCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (roomCode.length !== 6) {
      setRoomCodeError('Room code must be 6 characters');
      return;
    }
    if (onMultiplayerJoin) {
      onMultiplayerJoin(roomCode);
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (selectedMode === GameMode.SINGLE_PLAYER) {
    return (
      <div className="game-mode-selector">
        <div className="mode-header">
          <button className="back-button" onClick={handleBackClick}>
            ← Back
          </button>
          <h2>Single Player</h2>
        </div>
        
        <div className="difficulty-selection">
          <h3>Choose Difficulty</h3>
          <div className="difficulty-buttons">
            <button
              className="difficulty-button difficulty-button--easy"
              onClick={() => handleDifficultySelect(AIDifficulty.EASY)}
              disabled={isLoading}
            >
              <span className="difficulty-title">Easy</span>
              <span className="difficulty-description">AI makes mistakes</span>
            </button>
            
            <button
              className="difficulty-button difficulty-button--medium"
              onClick={() => handleDifficultySelect(AIDifficulty.MEDIUM)}
              disabled={isLoading}
            >
              <span className="difficulty-title">Medium</span>
              <span className="difficulty-description">Balanced gameplay</span>
            </button>
            
            <button
              className="difficulty-button difficulty-button--hard"
              onClick={() => handleDifficultySelect(AIDifficulty.HARD)}
              disabled={isLoading}
            >
              <span className="difficulty-title">Hard</span>
              <span className="difficulty-description">Unbeatable AI</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedMode === GameMode.MULTIPLAYER) {
    return (
      <div className="game-mode-selector">
        <div className="mode-header">
          <button className="back-button" onClick={handleBackClick}>
            ← Back
          </button>
          <h2>Multiplayer</h2>
        </div>
        
        <div className="multiplayer-options">
          <div className="multiplayer-section">
            <h3>Create New Game</h3>
            <button
              className="create-game-button"
              onClick={onMultiplayerCreate}
              disabled={isLoading}
            >
              Create Room
            </button>
          </div>
          
          <div className="multiplayer-section">
            <h3>Join with Room Code</h3>
            <form onSubmit={handleRoomCodeSubmit} className="room-code-form">
              <input
                type="text"
                value={roomCode}
                onChange={handleRoomCodeChange}
                placeholder="Enter 6-character code"
                className={`room-code-input ${roomCodeError ? 'room-code-input--error' : ''}`}
                maxLength={6}
                disabled={isLoading}
              />
              <button
                type="submit"
                className="join-button"
                disabled={isLoading || roomCode.length !== 6}
              >
                Join Game
              </button>
            </form>
            {roomCodeError && (
              <div className="error-message">{roomCodeError}</div>
            )}
          </div>
          
          {lobbyGames.length > 0 && (
            <div className="multiplayer-section">
              <h3>Available Games</h3>
              <div className="lobby-games">
                {lobbyGames.map((game) => (
                  <button
                    key={game.id}
                    className="lobby-game-item"
                    onClick={() => onLobbyGameSelect && onLobbyGameSelect(game)}
                    disabled={isLoading}
                  >
                    <div className="lobby-game-info">
                      <span className="lobby-game-host">{game.hostPlayer.name}</span>
                      <span className="lobby-game-time">
                        {formatTimeAgo(game.createdAt)}
                      </span>
                    </div>
                    <div className="lobby-game-settings">
                      {game.settings.realTimeMode ? 'Real-time' : 'Turn-based'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="game-mode-selector">
      <div className="mode-header">
        <h2>⭕ Tic-Tac-Toe</h2>
        {!isAuthenticated && guestGamesRemaining > 0 && (
          <p className="guest-info">
            Guest games remaining: {guestGamesRemaining}
          </p>
        )}
      </div>
      
      <div className="mode-buttons">
        <button
          className="mode-button mode-button--single"
          onClick={handleSinglePlayerClick}
          disabled={isLoading}
        >
          <span className="mode-icon">🤖</span>
          <span className="mode-title">Single Player</span>
          <span className="mode-description">Play against AI</span>
        </button>
        
        <button
          className="mode-button mode-button--multi"
          onClick={handleMultiplayerClick}
          disabled={isLoading || !WebSocketService.isAvailable()}
          title={!WebSocketService.isAvailable() ? 'Multiplayer not available - WebSocket not configured' : ''}
        >
          <span className="mode-icon">🌐</span>
          <span className="mode-title">Multiplayer</span>
          <span className="mode-description">
            {WebSocketService.isAvailable() ? 'Real-time cross-browser' : 'Coming soon'}
          </span>
        </button>
        
        {onJoinGame && (
          <button
            className="mode-button mode-button--join"
            onClick={onJoinGame}
            disabled={isLoading || !WebSocketService.isAvailable()}
            title={!WebSocketService.isAvailable() ? 'Multiplayer not available - WebSocket not configured' : ''}
          >
            <span className="mode-icon">🔗</span>
            <span className="mode-title">Join Game</span>
            <span className="mode-description">Enter room code</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default GameModeSelector;